import React from "react";

import { useTranslation } from "react-i18next";
import { useReducedMotion } from "framer-motion";

import {
  House,
  Wrench,
  Landmark,
  ChartNoAxesCombined,
  ArrowUpRight,
} from "lucide-react";
import ecosystemLogo from "../../../../assets/home/logo-without-text.svg";

import "./StakeholderEcosystemSection.css";
import { gsap } from "gsap";

const STAKEHOLDERS = [
  {
    key: "homebuyers",
    position: "top",
    Icon: House,
    fallbackTitle: "Homebuyers",
    fallbackDescription:
      "Create a digital dossier, organise property and financial information, upload documents and follow the renovation and financing journey from one place.",
    fallbackMeta: "Dossier · renovation · financing",
  },

  {
    key: "oss",
    position: "right",
    Icon: Wrench,
    fallbackTitle: "One-Stop-Shops",
    fallbackDescription:
      "Support homeowners with renovation planning, project development, coordination and the information needed to move renovation projects forward.",
    fallbackMeta: "Planning · support · coordination",
  },

  {
    key: "lenders",
    position: "bottom",
    Icon: Landmark,
    fallbackTitle: "Lenders",
    fallbackDescription:
      "Review relevant dossier information, manage loan cases and provide financing decisions and terms linked to the renovation journey.",
    fallbackMeta: "Assessment · loan cases · terms",
  },

  {
    key: "investors",
    position: "left",
    Icon: ChartNoAxesCombined,
    fallbackTitle: "Investors",
    fallbackDescription:
      "Gain a clearer view of renovation finance activity and the pathways connecting capital, lending and energy-efficient housing investment.",
    fallbackMeta: "Capital · insight · investment",
  },
];

/* =========================================================
   CARD
   ========================================================= */

function StakeholderCard({ item, t, isOpen, onToggle, cardRef }) {
  const {
    key,
    position,
    Icon,
    fallbackTitle,
    fallbackDescription,
    fallbackMeta,
  } = item;

  const title = t(`stakeholders.cards.${key}.title`, fallbackTitle);

  const description = t(
    `stakeholders.cards.${key}.description`,
    fallbackDescription,
  );

  const meta = t(`stakeholders.cards.${key}.meta`, fallbackMeta);

  return (
    <div
      ref={cardRef}
      className={`stakeholder-card stakeholder-card--${position}${
        isOpen ? " is-open" : ""
      }`}
      data-stakeholder={key}
      tabIndex={0}
      role="button"
      aria-expanded={isOpen}
      onClick={() => {
        onToggle(key);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();

          onToggle(key);
        }
      }}
    >
      <div className="stakeholder-card__head">
        <span className="stakeholder-card__icon" aria-hidden="true">
          <Icon size={22} strokeWidth={1.9} />
        </span>

        <span className="stakeholder-card__title">{title}</span>

        <span className="stakeholder-card__arrow" aria-hidden="true">
          <ArrowUpRight size={17} strokeWidth={2} />
        </span>
      </div>

      <div className="stakeholder-card__details">
        <div className="stakeholder-card__divider" />

        <p className="stakeholder-card__description">{description}</p>

        <span className="stakeholder-card__meta">{meta}</span>
      </div>
    </div>
  );
}

export default function StakeholderEcosystemSection() {
  const { t, i18n } = useTranslation("home");

  const prefersReducedMotion = useReducedMotion();

  const sectionRef = React.useRef(null);

  const introRef = React.useRef(null);

  const stageRef = React.useRef(null);

  const connectionsRef = React.useRef(null);

  const logoVisualRef = React.useRef(null);

  const cardRefs = React.useRef([]);

  const pathRefs = React.useRef([]);

  const [openCard, setOpenCard] = React.useState(null);

  const [allCardsOpen, setAllCardsOpen] = React.useState(false);

  const handleToggle = React.useCallback(
    (key) => {
      if (allCardsOpen) {
        return;
      }

      setOpenCard((current) => (current === key ? null : key));
    },

    [allCardsOpen],
  );

  /* =========================================================
     ALL ECOSYSTEM ANIMATION
     ========================================================= */

  React.useLayoutEffect(() => {
    const section = sectionRef.current;

    const intro = introRef.current;

    const stage = stageRef.current;

    const connectionSvg = connectionsRef.current;

    const logoVisual = logoVisualRef.current;

    const cards = cardRefs.current.filter(Boolean);

    const paths = pathRefs.current.filter(Boolean);

    const isMobile = window.matchMedia("(max-width: 660px)").matches;

    if (
      !section ||
      !intro ||
      !stage ||
      !connectionSvg ||
      !logoVisual ||
      cards.length !== 4 ||
      paths.length !== 4
    ) {
      return undefined;
    }

    /* =====================================================
       INTRO ELEMENTS
       ===================================================== */

    const introElements = [
      intro.querySelector(".stakeholder-ecosystem__eyebrow"),

      intro.querySelector(".stakeholder-ecosystem__title"),

      intro.querySelector(".stakeholder-ecosystem__lead"),
    ].filter(Boolean);

    const CONNECTION_GAP = 28;

    if (isMobile) {
      setAllCardsOpen(false);
      setOpenCard(null);

      /* ---------------------------------------------
     Reduced motion
     --------------------------------------------- */

      if (prefersReducedMotion) {
        gsap.set(introElements, {
          opacity: 1,
          visibility: "visible",
          y: 0,
        });

        gsap.set(logoVisual, {
          opacity: 1,
          visibility: "visible",
          scale: 1,
          rotation: 0,
        });

        gsap.set(cards, {
          opacity: 1,
          visibility: "visible",
          x: 0,
          y: 0,
          scale: 1,
        });

        gsap.set(paths, {
          opacity: 0,
          visibility: "hidden",
        });

        return undefined;
      }

      /* ---------------------------------------------
     Initial hidden states
     --------------------------------------------- */

      gsap.set(introElements, {
        opacity: 0,
        visibility: "hidden",
        y: 22,
      });

      gsap.set(logoVisual, {
        opacity: 0,
        visibility: "hidden",
        scale: 0.78,
        rotation: -4,

        transformOrigin: "50% 50%",
      });

      cards.forEach((card, index) => {
        gsap.set(card, {
          opacity: 0,
          visibility: "hidden",

          x: index % 2 === 0 ? -18 : 18,

          y: 16,

          scale: 0.985,
        });
      });

      gsap.set(paths, {
        opacity: 0,
        visibility: "hidden",
      });

      /* ---------------------------------------------
     Mobile timeline
     --------------------------------------------- */

      const mobileTimeline = gsap.timeline({
        paused: true,

        defaults: {
          overwrite: "auto",
        },
      });

      mobileTimeline.to(
        introElements,
        {
          opacity: 1,
          visibility: "visible",

          y: 0,

          duration: 0.48,

          stagger: 0.07,

          ease: "power3.out",
        },
        0,
      );

      mobileTimeline.to(
        logoVisual,
        {
          opacity: 1,
          visibility: "visible",

          scale: 1,
          rotation: 0,

          duration: 0.6,

          ease: "back.out(1.4)",
        },
        0.18,
      );

      mobileTimeline.to(
        cards,
        {
          opacity: 1,
          visibility: "visible",

          x: 0,
          y: 0,

          scale: 1,

          duration: 0.44,

          stagger: 0.12,

          ease: "power3.out",
        },
        0.46,
      );

      let hasPlayed = false;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !hasPlayed) {
            hasPlayed = true;

            mobileTimeline.restart();
          }
        },
        {
          threshold: 0.15,
        },
      );

      observer.observe(section);

      /* ---------------------------------------------
     Cleanup
     --------------------------------------------- */

      return () => {
        observer.disconnect();

        mobileTimeline.kill();

        gsap.set(introElements, {
          opacity: 1,
          visibility: "visible",
          y: 0,
        });

        gsap.set(logoVisual, {
          opacity: 1,
          visibility: "visible",
          scale: 1,
          rotation: 0,
        });

        gsap.set(cards, {
          opacity: 1,
          visibility: "visible",

          x: 0,
          y: 0,

          scale: 1,
        });
      };
    }

    /* =====================================================
       CONNECTION GEOMETRY
       ===================================================== */

    const getRectangleEdgeDistance = (rect, unitX, unitY) => {
      const EPSILON = 0.0001;

      const horizontalDistance =
        Math.abs(unitX) > EPSILON
          ? rect.width / 2 / Math.abs(unitX)
          : Number.POSITIVE_INFINITY;

      const verticalDistance =
        Math.abs(unitY) > EPSILON
          ? rect.height / 2 / Math.abs(unitY)
          : Number.POSITIVE_INFINITY;

      return Math.min(horizontalDistance, verticalDistance);
    };

    const updateConnectionGeometry = () => {
      const stageRect = stage.getBoundingClientRect();
      const isMobile = window.innerWidth <= 660;
      if (!stageRect.width || !stageRect.height) {
        return;
      }

      connectionSvg.setAttribute(
        "viewBox",
        `0 0 ${stageRect.width} ${stageRect.height}`,
      );

      const connections = [
        [cards[0], cards[1]],
        [cards[1], cards[2]],
        [cards[2], cards[3]],
        [cards[3], cards[0]],
      ];

      connections.forEach(([fromCard, toCard], index) => {
        const fromRect = fromCard.getBoundingClientRect();

        const toRect = toCard.getBoundingClientRect();

        if (isMobile && index === 3) {
          paths[index].setAttribute("d", "");
          return;
        }

        const fromX = fromRect.left - stageRect.left + fromRect.width / 2;

        const fromY = fromRect.top - stageRect.top + fromRect.height / 2;

        const toX = toRect.left - stageRect.left + toRect.width / 2;

        const toY = toRect.top - stageRect.top + toRect.height / 2;

        const deltaX = toX - fromX;

        const deltaY = toY - fromY;

        const distance = Math.hypot(deltaX, deltaY);

        if (!distance) {
          return;
        }

        const unitX = deltaX / distance;

        const unitY = deltaY / distance;

        const fromEdge = getRectangleEdgeDistance(fromRect, unitX, unitY);

        const toEdge = getRectangleEdgeDistance(toRect, unitX, unitY);

        const startX = fromX + unitX * (fromEdge + CONNECTION_GAP);

        const startY = fromY + unitY * (fromEdge + CONNECTION_GAP);

        const endX = toX - unitX * (toEdge + CONNECTION_GAP);

        const endY = toY - unitY * (toEdge + CONNECTION_GAP);

        paths[index].setAttribute(
          "d",
          [`M ${startX} ${startY}`, `L ${endX} ${endY}`].join(" "),
        );
      });
    };

    /* =====================================================
       PATH LENGTHS
       ===================================================== */

    updateConnectionGeometry();

    let pathLengths = paths.map((path) => {
      const length = path.getTotalLength();

      path.style.strokeDasharray = `${length}`;

      return length;
    });

    const refreshPathLengths = () => {
      pathLengths = paths.map((path) => {
        const length = path.getTotalLength();

        path.style.strokeDasharray = `${length}`;

        return length;
      });
    };

    /* =====================================================
       FINAL VISIBLE STATE
       ===================================================== */

    const showFinalState = () => {
      gsap.set(introElements, {
        opacity: 1,
        visibility: "visible",
        y: 0,
      });

      gsap.set(logoVisual, {
        opacity: 1,
        visibility: "visible",
        scale: 1,
        rotation: 0,
      });

      gsap.set(cards, {
        opacity: 1,
        visibility: "visible",
        y: 0,
        scale: 1,
      });

      paths.forEach((path) => {
        gsap.set(path, {
          strokeDashoffset: 0,
          opacity: 1,
          visibility: "visible",
        });
      });
    };

    /* =====================================================
       RESET BEFORE REPLAY
       ===================================================== */

    const resetForReveal = () => {
      /*
       * Header enters from below.
       */
      gsap.set(introElements, {
        opacity: 0,
        visibility: "hidden",
        y: 28,
      });

      /*
       * IMPORTANT:
       * animate only logoVisual.
       * The absolute centring wrapper stays untouched.
       */
      gsap.set(logoVisual, {
        opacity: 0,
        visibility: "hidden",
        scale: 0.76,
        rotation: -3,
        transformOrigin: "50% 50%",
      });

      gsap.set(cards, {
        opacity: 0,
        visibility: "hidden",
        y: 12,
        scale: 0.965,
      });

      paths.forEach((path, index) => {
        gsap.set(path, {
          strokeDasharray: pathLengths[index],

          strokeDashoffset: pathLengths[index],

          opacity: 0,
          visibility: "hidden",
        });
      });
    };

    /* =====================================================
       REDUCED MOTION
       ===================================================== */

    if (prefersReducedMotion) {
      showFinalState();

      setAllCardsOpen(true);

      return () => {
        setAllCardsOpen(false);
      };
    }

    /* =====================================================
       MAIN TIMELINE
       ===================================================== */

    let sequenceFinished = false;

    const timeline = gsap.timeline({
      paused: true,

      defaults: {
        overwrite: "auto",
      },
    });

    /* =====================================================
       INTRO

       Eyebrow -> title -> subtitle
       ===================================================== */

    timeline.to(
      introElements,

      {
        opacity: 1,

        visibility: "visible",

        y: 0,

        stagger: 0.08,

        duration: 0.5,

        ease: "power3.out",
      },

      0,
    );

    /* =====================================================
       LOGO
       ===================================================== */

    timeline.to(
      logoVisual,

      {
        opacity: 1,

        visibility: "visible",

        scale: 1,

        rotation: 0,

        duration: 0.62,

        ease: "back.out(1.3)",
      },

      0.2,
    );

    /* =====================================================
       HOMEBUYERS
       ===================================================== */

    timeline.to(
      cards[0],

      {
        opacity: 1,

        visibility: "visible",

        y: 0,

        scale: 1,

        duration: 0.3,

        ease: "power3.out",
      },

      0.52,
    );

    /* =====================================================
       HOMEBUYERS -> OSS
       ===================================================== */

    timeline.to(
      paths[0],

      {
        strokeDashoffset: 0,

        opacity: 1,

        visibility: "visible",

        duration: 0.5,

        ease: "power1.inOut",
      },

      0.72,
    );

    /* =====================================================
       OSS
       ===================================================== */

    timeline.to(
      cards[1],

      {
        opacity: 1,

        visibility: "visible",

        y: 0,

        scale: 1,

        duration: 0.28,

        ease: "power3.out",
      },

      1.08,
    );

    /* =====================================================
       OSS -> LENDERS
       ===================================================== */

    timeline.to(
      paths[1],

      {
        strokeDashoffset: 0,

        opacity: 1,

        visibility: "visible",

        duration: 0.5,

        ease: "power1.inOut",
      },

      1.22,
    );

    /* =====================================================
       LENDERS
       ===================================================== */

    timeline.to(
      cards[2],

      {
        opacity: 1,

        visibility: "visible",

        y: 0,

        scale: 1,

        duration: 0.28,

        ease: "power3.out",
      },

      1.58,
    );

    /* =====================================================
       LENDERS -> INVESTORS
       ===================================================== */

    timeline.to(
      paths[2],

      {
        strokeDashoffset: 0,

        opacity: 1,

        visibility: "visible",

        duration: 0.5,

        ease: "power1.inOut",
      },

      1.72,
    );

    /* =====================================================
       INVESTORS
       ===================================================== */

    timeline.to(
      cards[3],

      {
        opacity: 1,

        visibility: "visible",

        y: 0,

        scale: 1,

        duration: 0.28,

        ease: "power3.out",
      },

      2.08,
    );

    /* =====================================================
       INVESTORS -> HOMEBUYERS
       ===================================================== */

    timeline.to(
      paths[3],

      {
        strokeDashoffset: 0,

        opacity: 1,

        visibility: "visible",

        duration: 0.52,

        ease: "power1.inOut",
      },

      2.22,
    );

    /* =====================================================
       ALL CARDS OPEN TOGETHER
       ===================================================== */

    timeline.call(
      () => {
        setOpenCard(null);

        setAllCardsOpen(true);

        sequenceFinished = true;
      },

      [],

      2.78,
    );

    /* =====================================================
       SCROLL VISIT STATE
       ===================================================== */

    let insideActivationZone = false;

    let scrollFrame = null;

    let replayFrame = null;

    let geometryFrame = null;

    /* =====================================================
       CARD SIZE OBSERVER
       ===================================================== */

    const cardResizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            if (!sequenceFinished) {
              return;
            }

            if (geometryFrame) {
              cancelAnimationFrame(geometryFrame);
            }

            geometryFrame = requestAnimationFrame(() => {
              geometryFrame = null;

              updateConnectionGeometry();

              refreshPathLengths();

              paths.forEach((path) => {
                gsap.set(path, {
                  strokeDashoffset: 0,

                  opacity: 1,

                  visibility: "visible",
                });
              });
            });
          })
        : null;

    cards.forEach((card) => {
      cardResizeObserver?.observe(card);
    });

    /* =====================================================
       REPLAY
       ===================================================== */

    const replay = () => {
      setOpenCard(null);

      setAllCardsOpen(false);

      sequenceFinished = false;

      timeline.pause(0);

      updateConnectionGeometry();

      refreshPathLengths();

      resetForReveal();

      if (replayFrame) {
        cancelAnimationFrame(replayFrame);
      }

      replayFrame = requestAnimationFrame(() => {
        timeline.restart();
      });
    };

    /* =====================================================
       VIEWPORT CHECK
       ===================================================== */

    const checkPosition = () => {
      scrollFrame = null;

      const sectionRect = section.getBoundingClientRect();

      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;

      const inActivationZone =
        sectionRect.top <= viewportHeight * 0.9 &&
        sectionRect.bottom >= viewportHeight * 0.1;

      if (inActivationZone && !insideActivationZone) {
        insideActivationZone = true;

        replay();

        return;
      }

      const fullyOutside =
        sectionRect.bottom <= 0 || sectionRect.top >= viewportHeight;

      if (fullyOutside && insideActivationZone) {
        insideActivationZone = false;

        sequenceFinished = false;

        setOpenCard(null);

        setAllCardsOpen(false);

        timeline.pause(0);

        /*
         * Fail-visible while outside.
         *
         * When entering again replay() will
         * establish the hidden start states.
         */
        showFinalState();
      }
    };

    const scheduleCheck = () => {
      if (scrollFrame) {
        return;
      }

      scrollFrame = requestAnimationFrame(checkPosition);
    };

    /* =====================================================
       RESIZE
       ===================================================== */

    const handleResize = () => {
      updateConnectionGeometry();

      refreshPathLengths();

      if (sequenceFinished) {
        paths.forEach((path) => {
          gsap.set(path, {
            strokeDashoffset: 0,

            opacity: 1,

            visibility: "visible",
          });
        });
      }

      scheduleCheck();
    };

    window.addEventListener("scroll", scheduleCheck, {
      passive: true,
    });

    window.addEventListener("resize", handleResize);

    /* =====================================================
       INITIALISE
       ===================================================== */

    const initialise = () => {
      updateConnectionGeometry();

      refreshPathLengths();

      const sectionRect = section.getBoundingClientRect();

      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;

      const currentlyVisible =
        sectionRect.top < viewportHeight && sectionRect.bottom > 0;

      /*
       * Always start from a safe visible state.
       */
      showFinalState();

      if (currentlyVisible) {
        insideActivationZone = true;

        replay();
      }

      scheduleCheck();
    };

    let secondInitFrame = null;

    const initFrame = requestAnimationFrame(() => {
      secondInitFrame = requestAnimationFrame(initialise);
    });

    /*
     * Re-measure once fonts/layout have settled.
     */
    const delayedCheck = window.setTimeout(
      () => {
        updateConnectionGeometry();

        refreshPathLengths();

        scheduleCheck();
      },

      350,
    );

    /* =====================================================
       CLEANUP
       ===================================================== */

    return () => {
      cancelAnimationFrame(initFrame);

      if (secondInitFrame) {
        cancelAnimationFrame(secondInitFrame);
      }

      if (scrollFrame) {
        cancelAnimationFrame(scrollFrame);
      }

      if (replayFrame) {
        cancelAnimationFrame(replayFrame);
      }

      if (geometryFrame) {
        cancelAnimationFrame(geometryFrame);
      }

      cardResizeObserver?.disconnect();

      window.clearTimeout(delayedCheck);

      window.removeEventListener("scroll", scheduleCheck);

      window.removeEventListener("resize", handleResize);

      timeline.kill();

      /*
       * Do NOT leave anything hidden when
       * React destroys/recreates this effect.
       */
      showFinalState();
    };
  }, [i18n.resolvedLanguage, prefersReducedMotion]);

  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <section
      ref={sectionRef}
      className="stakeholder-ecosystem"
      aria-labelledby="stakeholder-ecosystem-title"
    >
      <div className="stakeholder-ecosystem__inner">
        {/* =================================================
            INTRO
            ================================================= */}

        <div ref={introRef} className="stakeholder-ecosystem__intro">
          <span className="stakeholder-ecosystem__eyebrow">
            {t("stakeholders.eyebrow", "STAKEHOLDER ECOSYSTEM")}
          </span>

          <h2
            id="stakeholder-ecosystem-title"
            className="stakeholder-ecosystem__title"
          >
            <span>
              {t(
                "stakeholders.title.prefix",
                "How the LEG-UP platform connects all",
              )}{" "}
            </span>

            <strong>{t("stakeholders.title.accent", "four actors.")}</strong>
          </h2>

          <p className="stakeholder-ecosystem__lead">
            {t(
              "stakeholders.lead",

              "One coordinated ecosystem, connecting the people and organisations that shape the renovation and financing journey.",
            )}
          </p>
        </div>

        {/* =================================================
            STAGE
            ================================================= */}

        <div ref={stageRef} className="stakeholder-ecosystem__stage">
          {/* ===============================================
              CONNECTIONS
              =============================================== */}

          <svg
            ref={connectionsRef}
            className="stakeholder-ecosystem__connections"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              ref={(node) => {
                pathRefs.current[0] = node;
              }}
            />

            <path
              ref={(node) => {
                pathRefs.current[1] = node;
              }}
            />

            <path
              ref={(node) => {
                pathRefs.current[2] = node;
              }}
            />

            <path
              ref={(node) => {
                pathRefs.current[3] = node;
              }}
            />
          </svg>

          {/* ===============================================
              CENTRE LOGO

              Outer = permanent positioning.
              Inner = animation only.
              =============================================== */}

          <div className="stakeholder-ecosystem__logo" aria-hidden="true">
            <div
              ref={logoVisualRef}
              className="stakeholder-ecosystem__logo-visual"
            >
              <img src={ecosystemLogo} alt="" />
            </div>
          </div>

          {/* ===============================================
              CARDS
              =============================================== */}

          {STAKEHOLDERS.map((item, index) => (
            <StakeholderCard
              key={item.key}
              item={item}
              t={t}
              isOpen={allCardsOpen || openCard === item.key}
              onToggle={handleToggle}
              cardRef={(node) => {
                cardRefs.current[index] = node;
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
